/**
 * BillingTab Component
 * Billing address form and invoice list
 */

import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle, AlertCircle, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { CollapsibleGlassCard } from '@/components/common/GlassCard';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useBillingProfile, useUpdateBillingProfile, useInvoices, useInvoiceDetail } from '@/hooks/useBilling';
import type { UpdateBillingProfileRequest } from '@/types/profile';
import { cn } from '@/lib/utils';

const inputClassName =
  'h-12 bg-white border-gray-200 focus:border-[var(--color-primary-start)] focus:ring-[var(--color-primary-start)]';
const selectTriggerClassName =
  'h-12 bg-white border-gray-200 focus:border-[var(--color-primary-start)] focus:ring-[var(--color-primary-start)]';
const labelClassName = 'text-[var(--color-charcoal)] font-medium';

/** ISO 3166-1 alpha-2 country codes with translation keys */
const COUNTRY_OPTIONS = [
  { code: 'DE', labelKey: 'deutschland' },
  { code: 'AT', labelKey: 'oesterreich' },
  { code: 'CH', labelKey: 'schweiz' },
  { code: 'BE', labelKey: 'belgien' },
  { code: 'FR', labelKey: 'frankreich' },
  { code: 'IT', labelKey: 'italien' },
  { code: 'LU', labelKey: 'luxemburg' },
  { code: 'NL', labelKey: 'niederlande' },
  { code: 'PL', labelKey: 'polen' },
  { code: 'CZ', labelKey: 'tschechien' },
] as const;

const billingSchema = z.object({
  billingFirstName: z.string().min(1),
  billingLastName: z.string().min(1),
  billingEmail: z.string().email(),
  billingOrganization: z.string().min(1),
  billingSalutation: z.string().optional(),
  billingStreet: z.string().min(1),
  billingHouseNumber: z.string().min(1),
  billingHouseNumberAddition: z.string().optional(),
  billingCity: z.string().min(1),
  billingPostalCode: z.string().min(1),
  billingCountry: z.string().min(1),
  vatNumber: z.string().optional(),
});

type BillingFormData = z.infer<typeof billingSchema>;

const PAGE_SIZE = 10;

const statusColors: Record<string, string> = {
  Draft: 'bg-gray-100 text-gray-600',
  Issued: 'bg-blue-100 text-blue-700',
  Paid: 'bg-green-100 text-green-700',
  Cancelled: 'bg-gray-100 text-gray-500',
};

export function BillingTab() {
  const { t } = useTranslation();
  const [billingSuccess, setBillingSuccess] = useState(false);
  const [invoicePage, setInvoicePage] = useState(0);
  const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);

  const { data: billingProfile, isLoading: billingLoading } = useBillingProfile();
  const updateBilling = useUpdateBillingProfile();
  const { data: invoiceData, isLoading: invoicesLoading } = useInvoices(PAGE_SIZE, invoicePage * PAGE_SIZE);
  const { data: invoiceDetail } = useInvoiceDetail(expandedInvoice);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm<BillingFormData>({
    resolver: zodResolver(billingSchema),
    defaultValues: {
      billingFirstName: '',
      billingLastName: '',
      billingEmail: '',
      billingOrganization: '',
      billingSalutation: '',
      billingStreet: '',
      billingHouseNumber: '',
      billingHouseNumberAddition: '',
      billingCity: '',
      billingPostalCode: '',
      billingCountry: 'DE',
      vatNumber: '',
    },
  });

  // Populate form when billing profile loads
  useEffect(() => {
    if (billingProfile) {
      reset({
        billingFirstName: billingProfile.billingFirstName ?? '',
        billingLastName: billingProfile.billingLastName ?? '',
        billingEmail: billingProfile.billingEmail ?? '',
        billingOrganization: billingProfile.billingOrganization ?? '',
        billingSalutation: billingProfile.billingSalutation ?? '',
        billingStreet: billingProfile.billingStreet ?? '',
        billingHouseNumber: billingProfile.billingHouseNumber ?? '',
        billingHouseNumberAddition: billingProfile.billingHouseNumberAddition ?? '',
        billingCity: billingProfile.billingCity ?? '',
        billingPostalCode: billingProfile.billingPostalCode ?? '',
        billingCountry: billingProfile.billingCountry ?? 'DE',
        vatNumber: billingProfile.vatNumber ?? '',
      });
    }
  }, [billingProfile, reset]);

  const onBillingSubmit = (data: BillingFormData) => {
    const payload: UpdateBillingProfileRequest = {
      billingFirstName: data.billingFirstName,
      billingLastName: data.billingLastName,
      billingEmail: data.billingEmail,
      billingOrganization: data.billingOrganization,
      billingSalutation: data.billingSalutation || undefined,
      billingStreet: data.billingStreet,
      billingHouseNumber: data.billingHouseNumber,
      billingHouseNumberAddition: data.billingHouseNumberAddition || undefined,
      billingCity: data.billingCity,
      billingPostalCode: data.billingPostalCode,
      billingCountry: data.billingCountry,
      vatNumber: data.vatNumber || undefined,
    };
    updateBilling.mutate(payload, {
      onSuccess: () => {
        setBillingSuccess(true);
        setTimeout(() => setBillingSuccess(false), 3000);
      },
    });
  };

  const formatCurrency = (amount: number, cur: string) =>
    amount.toLocaleString('de-DE', { style: 'currency', currency: cur || 'EUR' });

  return (
    <div className="space-y-6">
      {/* Billing Address */}
      <CollapsibleGlassCard title={t('profile.billing.title')}>
          {billingLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {billingSuccess && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  {t('profile.billing.updateSuccess')}
                </div>
              )}

              <form onSubmit={handleSubmit(onBillingSubmit)}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className={labelClassName}>{t('profile.billing.billingFirstName')}</Label>
                    <Input {...register('billingFirstName')} className={inputClassName} />
                    {errors.billingFirstName && <p className="text-sm text-red-500 mt-1">{t('validation.fieldRequired', { field: t('profile.billing.billingFirstName') })}</p>}
                  </div>
                  <div>
                    <Label className={labelClassName}>{t('profile.billing.billingLastName')}</Label>
                    <Input {...register('billingLastName')} className={inputClassName} />
                    {errors.billingLastName && <p className="text-sm text-red-500 mt-1">{t('validation.fieldRequired', { field: t('profile.billing.billingLastName') })}</p>}
                  </div>
                  <div>
                    <Label className={labelClassName}>{t('profile.billing.billingEmail')}</Label>
                    <Input {...register('billingEmail')} type="email" className={inputClassName} />
                    {errors.billingEmail && <p className="text-sm text-red-500 mt-1">{t('validation.invalidEmail')}</p>}
                  </div>
                  <div>
                    <Label className={labelClassName}>{t('profile.billing.organization')}</Label>
                    <Input {...register('billingOrganization')} className={inputClassName} />
                    {errors.billingOrganization && <p className="text-sm text-red-500 mt-1">{t('validation.fieldRequired', { field: t('profile.billing.organization') })}</p>}
                  </div>
                  <div>
                    <Label className={labelClassName}>{t('profile.billing.salutation')}</Label>
                    <Input {...register('billingSalutation')} className={inputClassName} placeholder={t('profile.billing.salutationPlaceholder')} />
                  </div>
                  <div>
                    <Label className={labelClassName}>{t('profile.billing.vatNumber')}</Label>
                    <Input {...register('vatNumber')} className={inputClassName} placeholder="DE123456789" />
                  </div>
                </div>

                {/* Address section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div className="sm:col-span-2">
                    <div className="grid grid-cols-[3fr_1fr_1fr] gap-4">
                      <div>
                        <Label className={labelClassName}>{t('profile.billing.street')}</Label>
                        <Input {...register('billingStreet')} className={inputClassName} />
                        {errors.billingStreet && <p className="text-sm text-red-500 mt-1">{t('validation.fieldRequired', { field: t('profile.billing.street') })}</p>}
                      </div>
                      <div>
                        <Label className={labelClassName}>{t('profile.billing.houseNumber')}</Label>
                        <Input {...register('billingHouseNumber')} className={inputClassName} />
                        {errors.billingHouseNumber && <p className="text-sm text-red-500 mt-1">{t('validation.fieldRequired', { field: t('profile.billing.houseNumber') })}</p>}
                      </div>
                      <div>
                        <Label className={labelClassName}>{t('profile.billing.houseNumberAddition')}</Label>
                        <Input {...register('billingHouseNumberAddition')} className={inputClassName} />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className={labelClassName}>{t('profile.billing.postalCode')}</Label>
                    <Input {...register('billingPostalCode')} className={inputClassName} />
                    {errors.billingPostalCode && <p className="text-sm text-red-500 mt-1">{t('validation.fieldRequired', { field: t('profile.billing.postalCode') })}</p>}
                  </div>
                  <div>
                    <Label className={labelClassName}>{t('profile.billing.city')}</Label>
                    <Input {...register('billingCity')} className={inputClassName} />
                    {errors.billingCity && <p className="text-sm text-red-500 mt-1">{t('validation.fieldRequired', { field: t('profile.billing.city') })}</p>}
                  </div>
                  <div>
                    <Label className={labelClassName}>{t('profile.billing.country')}</Label>
                    <Controller
                      name="billingCountry"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger className={cn(selectTriggerClassName, errors.billingCountry && 'border-red-500')}>
                            <SelectValue placeholder={t('profile.billing.country')} />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRY_OPTIONS.map(({ code, labelKey }) => (
                              <SelectItem key={code} value={code}>
                                {t(`credits.checkout.countryOptions.${labelKey}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.billingCountry && <p className="text-sm text-red-500 mt-1">{t('validation.fieldRequired', { field: t('profile.billing.country') })}</p>}
                  </div>
                </div>

                <div className="flex justify-end mt-4">
                  <Button
                    type="submit"
                    className="btn-primary"
                    disabled={updateBilling.isPending || !isDirty}
                  >
                    {updateBilling.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    {t('profile.billing.save')}
                  </Button>
                </div>

                {updateBilling.isError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {t('profile.billing.updateError')}
                  </div>
                )}
              </form>
            </>
          )}
      </CollapsibleGlassCard>

      {/* Invoices */}
      <CollapsibleGlassCard title={t('profile.billing.invoices')} defaultOpen={false}>
          {invoicesLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : !invoiceData?.invoices?.length ? (
            <p className="text-gray-500 text-sm py-4 text-center">
              {t('profile.billing.noInvoices')}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-3 px-2 font-medium text-gray-500">{t('profile.billing.invoiceNumber')}</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-500">{t('profile.billing.invoiceDate')}</th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500">{t('profile.billing.invoiceAmount')}</th>
                      <th className="text-center py-3 px-2 font-medium text-gray-500">{t('profile.billing.invoiceStatus')}</th>
                      <th className="py-3 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceData.invoices.map((inv) => (
                      <>
                        <tr
                          key={inv.id}
                          className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer"
                          onClick={() => setExpandedInvoice(expandedInvoice === inv.id ? null : inv.id)}
                        >
                          <td className="py-3 px-2 font-medium">{inv.invoiceNumber}</td>
                          <td className="py-3 px-2">{new Date(inv.createdAt).toLocaleDateString()}</td>
                          <td className="py-3 px-2 text-right">
                            {formatCurrency(inv.total, inv.currency)}
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className={cn('inline-block px-2 py-0.5 rounded-full text-xs font-medium', statusColors[inv.status] ?? 'bg-gray-100 text-gray-600')}>
                              {t(`profile.billing.status.${inv.status}`)}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            {expandedInvoice === inv.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                          </td>
                        </tr>
                        {expandedInvoice === inv.id && invoiceDetail && (
                          <tr key={`${inv.id}-detail`}>
                            <td colSpan={5} className="p-4 bg-gray-50/50">
                              <div className="space-y-3">
                                <h4 className="font-medium text-sm">{t('profile.billing.lineItems')}</h4>
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="border-b border-gray-200">
                                      <th className="text-left py-2">{t('profile.billing.description')}</th>
                                      <th className="text-right py-2">{t('profile.billing.qty')}</th>
                                      <th className="text-right py-2">{t('profile.billing.unitPrice')}</th>
                                      <th className="text-right py-2">{t('profile.billing.totalPrice')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {invoiceDetail.lineItems?.map((item) => (
                                      <tr key={item.id} className="border-b border-gray-100">
                                        <td className="py-2">{item.description}</td>
                                        <td className="py-2 text-right">{item.quantity}</td>
                                        <td className="py-2 text-right">{item.unitPrice.toFixed(2)} {invoiceDetail.currency}</td>
                                        <td className="py-2 text-right">{item.lineTotal.toFixed(2)} {invoiceDetail.currency}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                <div className="flex justify-end text-xs space-x-8">
                                  <span>{t('profile.billing.subtotal')}: {invoiceDetail.subtotal?.toFixed(2)} {invoiceDetail.currency}</span>
                                  <span>{t('profile.billing.vat')} ({Math.round(invoiceDetail.taxRate * 100)}%): {invoiceDetail.taxAmount?.toFixed(2)} {invoiceDetail.currency}</span>
                                  <span className="font-bold">{t('profile.billing.invoiceTotal')}: {invoiceDetail.total?.toFixed(2)} {invoiceDetail.currency}</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {invoiceData.total > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setInvoicePage((p) => Math.max(0, p - 1))}
                    disabled={invoicePage === 0}
                    className="gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    {t('profile.credits.previous')}
                  </Button>
                  <span className="text-sm text-gray-500">
                    {invoicePage + 1} / {Math.ceil(invoiceData.total / PAGE_SIZE)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setInvoicePage((p) => p + 1)}
                    disabled={(invoicePage + 1) * PAGE_SIZE >= invoiceData.total}
                    className="gap-1"
                  >
                    {t('profile.credits.next')}
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
      </CollapsibleGlassCard>
    </div>
  );
}
